import type { Paths } from '@src/utils/types';
import { createContext, useContext, type ReactNode } from 'react';
import type { EntityAdminRecord } from '../../types';
import type { InputsAndDropdownsDrawerOptions } from '../InputsAndDropdownsDrawer/types';

export interface CustomDrawerContextValue<TDrawerRecord extends EntityAdminRecord> {
  form: TDrawerRecord;
  isEditing: boolean;
  onFormUpdate: (field: Paths<TDrawerRecord>, value: unknown) => void;
  inheritedValues: Record<Paths<TDrawerRecord>, string | undefined>;
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>;
  /** The original entity that was fetched from the server. Undefined for new entities. */
  originalEntity: TDrawerRecord | undefined;
  errorState: Record<string, string | undefined> | null;
  touchState: Record<string, boolean>;
  /** Whether this drawer is for adding a child entity */
  addingChildEntity: boolean;
  /** Register visible tabs for validation filtering */
  setVisibleTabs: (tabs: string[]) => void;
}

// Using 'any' here because React contexts don't work well with generics at creation time.
// The actual type safety is provided by the useCustomDrawer hook which casts to the correct type.
const CustomDrawerContext = createContext<CustomDrawerContextValue<any> | undefined>(undefined);

export function CustomDrawerProvider<TDrawerRecord extends EntityAdminRecord>({
  children,
  value,
}: {
  children: ReactNode;
  value: CustomDrawerContextValue<TDrawerRecord>;
}) {
  return <CustomDrawerContext.Provider value={value}>{children}</CustomDrawerContext.Provider>;
}

export function useCustomDrawer<TDrawerRecord extends EntityAdminRecord>(): CustomDrawerContextValue<TDrawerRecord> {
  const context = useContext(CustomDrawerContext);
  if (!context) {
    throw new Error(
      'useCustomDrawer must be used within a CustomDrawerProvider. Wrap your component with <CustomDrawerProvider>.'
    );
  }
  return context as CustomDrawerContextValue<TDrawerRecord>;
}
